import { css, cx } from "@emotion/css";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import LogoIcon from "img/logo.svg";
import LogoWithNameIcon from "img/logo_with_name.svg";
import { BookMarked } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
	generatePath,
	Link as RouterLink,
	useLocation,
} from "react-router-dom";
import routing from "routing";
import { isFaqEnabled } from "src/hooks/useAppConfig";
import ArticlesIcon from "src/images/Icons/newIcons/navigation/articles.svg";
import CourseIcon from "src/images/Icons/newIcons/navigation/courses.svg";
import ExamIcon from "src/images/Icons/newIcons/navigation/exams.svg";
import FlashcardIcon from "src/images/Icons/newIcons/navigation/flashcards.svg";
import HomeIcon from "src/images/Icons/newIcons/navigation/home.svg";
import isMobile from "../../helpers/isMobile";
import { isLdek, isLek } from "../../helpers/services";
import { IconButton } from "../Default/LekUI/Button";
import { Link } from "./Link";
import { NavbarFolders } from "./NavbarFolders";

interface LogoProps {
	open: boolean;
}

export const Logo = ({ open }: LogoProps) => {
	return (
		<ListItemButton
			component={RouterLink}
			to={routing.paths.home}
			className={cx(
				"p-0",
				css`
          background: transparent !important;
          width: ${open ? 215 : 45}px;
          height: 40px;
        `,
			)}
			disableRipple
		>
			{open ? (
				<LogoWithNameIcon
					className={css`
            width: 215px;
          `}
				/>
			) : (
				<LogoIcon
					className={cx(
						"icon-logo mb-4",
						css`
              width: 45px;
            `,
					)}
				/>
			)}
		</ListItemButton>
	);
};

interface MenuProps {
	open: boolean;
	setOpen: (open: boolean) => void;
}

const Menu = (props: MenuProps) => {
	const { t } = useTranslation();
	const location = useLocation();

	const isSelected = (path: string) => {
		return location.pathname.startsWith(`/${path.split("/")[1]}`);
	};

	const toggleIsOpen = () => {
		props.setOpen(!props.open);
	};

	return (
		<Drawer
			variant={isMobile() ? "temporary" : "permanent"}
			open={props.open}
			className={cx("drawer border-right-2", {
				"drawer--open": props.open,
				"drawer--close": !props.open,
			})}
			onClose={toggleIsOpen}
		>
			<List>
				<Logo open={props.open} />
			</List>
			<div className="drawer--scrollable">
				<div>
					<IconButton
						className={cx(css`
              width: 45px;
              height: 45px;
              border-radius: 8px !important;

              svg {
                stroke: #818181;
                stroke-width: 1px;
              }
            `)}
						onClick={toggleIsOpen}
					>
						{props.open ? (
							<ChevronLeftIcon fontSize="small" />
						) : (
							<ChevronRightIcon fontSize="small" />
						)}
					</IconButton>
				</div>
				<List className="flex flex-col gap-4">
					<Link
						href={routing.paths.home}
						submenuId="home"
						Icon={<HomeIcon className="icon-big" />}
						className={cx("drawer--menu--item size-18-light", {
							"drawer--menu--item-short": !props.open,
						})}
					>
						{t("navbar.home")}
					</Link>
					{(isLek() || isLdek()) && (
						<Link
							href={routing.paths.knowledgeBase}
							submenuId="articles"
							Icon={<ArticlesIcon className="icon-big" />}
							selected={
								location.pathname.startsWith(routing.paths.knowledgeBase) ||
								isSelected(routing.paths.article)
							}
							className={cx("drawer--menu--item size-18-light", {
								"drawer--menu--item-short": !props.open,
							})}
						>
							{t("navbar.articles")}
						</Link>
					)}
					<Link
						href={routing.paths.exams}
						submenuId="finishedExams"
						Icon={<ExamIcon className="icon-big" />}
						selected={isSelected(routing.paths.exams)}
						className={cx("drawer--menu--item size-18-light", {
							"drawer--menu--item-short": !props.open,
						})}
						data-test-id="nav-exams-link"
					>
						{t("navbar.exams")}
					</Link>
					<Link
						href={routing.paths.flashcards}
						submenuId="flashcards"
						Icon={<FlashcardIcon className="icon-big" />}
						selected={isSelected(routing.paths.flashcards)}
						className={cx("drawer--menu--item size-18-light", {
							"drawer--menu--item-short": !props.open,
						})}
						data-test-id="nav-flashcards-link"
					>
						{t("navbar.flashcards")}
					</Link>
					<Link
						href={routing.paths.coursesList}
						submenuId="coursesList"
						Icon={<CourseIcon className="icon-big" />}
						selected={isSelected(routing.paths.coursesList)}
						className={cx("drawer--menu--item size-18-light", {
							"drawer--menu--item-short": !props.open,
						})}
					>
						{t("navbar.courses")}
					</Link>
					{isFaqEnabled() && (
						<Link
							href={generatePath(routing.paths.guide, {})}
							submenuId="guide"
							Icon={<BookMarked className="icon-big" />}
							selected={isSelected(routing.paths.guide)}
							className={cx("drawer--menu--item size-18-light", {
								"drawer--menu--item-short": !props.open,
							})}
							data-test-id="nav-guide-link"
						>
							<span className="flex items-center gap-8">
								{t("navbar.guide", "Poradnik")}
								<span className="success-box py-4 px-6 border-radius-8 size-12">
									{t("commands.news", "Nowość!")}
								</span>
							</span>
						</Link>
					)}
				</List>
				{!isMobile() && <NavbarFolders open={props.open} />}
			</div>
		</Drawer>
	);
};

export default Menu;
